import React, { useEffect, useRef, useState } from 'react';
import { GraduationCap, Flag } from 'lucide-react';
import * as styles from './aiEngineerFellowship.module.scss';
import { ICONS } from './iconRegistry';

const CareerOutcomes = ({ content }) => {
  const { steps, portfolioApps, skills } = content;
  const [activeIndex, setActiveIndex] = useState(0);
  const [count, setCount] = useState(0);
  const countRef = useRef(null);

  useEffect(() => {
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || typeof IntersectionObserver === 'undefined' || !countRef.current) {
      setCount(16);
      return undefined;
    }
    const target = 16;
    const node = countRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        let start = null;
        const tick = (ts) => {
          if (!start) start = ts;
          const p = Math.min((ts - start) / 900, 1);
          setCount(Math.round(p * target));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.5 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const activeTarget = steps[activeIndex].target;

  return (
    <section id="outcomes" className={styles.mint}>
      <div className={styles.wrap}>
        <div className={styles.shead}>
          <h2>Career Outcomes &amp; Support</h2>
          <p>
            A portfolio that reads like work experience — and career services, including 5
            referral interviews, to turn it into the next role. <b>Hover a step to see what it
            adds to your profile.</b>
          </p>
        </div>
        <div className={styles.oc}>
          <div className={styles.profile} aria-label="What your profile looks like at the end of the program">
            <div className={styles.pfTag}>Your profile on Day 180</div>
            <div className={styles.pfTop}>
              <div className={styles.pfAva}>You</div>
              <div className={styles.pfId}>
                <div className={styles.pfName}>Your name</div>
                <div className={styles.pfHead}>
                  AI Engineer · ships production LLM apps, agents &amp; evals · deployed on AWS
                </div>
              </div>
              <div className={`${styles.pfBadge} ${activeTarget === 'cert' ? styles.spot : ''}`}>
                <GraduationCap size={14} className="mr-1 inline -mt-0.5" aria-hidden="true" /> Crio certificate
              </div>
            </div>
            <div className={`${styles.pfBlock} ${activeTarget === 'portfolio' ? styles.spot : ''}`}>
              <div className={styles.pfH}>
                Portfolio <span className={styles.pfCount} ref={countRef}>{count}+ apps</span>
              </div>
              <div className={styles.pfApps}>
                {portfolioApps.map((app) => {
                  const Icon = ICONS[app.icon];
                  return (
                    <span key={app.label}>
                      {Icon && <Icon size={12} className="mr-1 inline -mt-0.5" aria-hidden="true" />}
                      {app.label}
                    </span>
                  );
                })}
                <span className={styles.more}>+10 mini projects</span>
                <span className={styles.pfCap}>
                  <Flag size={12} className="mr-1 inline -mt-0.5" aria-hidden="true" /> Capstone: your own product
                </span>
              </div>
              <div className={styles.pfSub}>
                Each one live — with an eval report, cost/latency dashboard and demo video.
                Hosted, linkable, reviewable.
              </div>
            </div>
            <div className={`${styles.pfBlock} ${activeTarget === 'skills' ? styles.spot : ''}`}>
              <div className={styles.pfH}>Skills hiring managers search for</div>
              <div className={styles.stackRow}>
                {skills.map((skill) => (
                  <span className={`${styles.chip} ${styles.g}`} key={skill}>
                    {skill}
                  </span>
                ))}
              </div>
            </div>
            <div className={`${styles.pfRow} ${activeTarget === 'interview' ? styles.spot : ''}`}>
              <b>Interview-ready</b>
              Agentic system design · problem decomposition · behavioural — practised with scored
              feedback.
            </div>
            <div className={`${styles.pfRow} ${activeTarget === 'referral' ? styles.spot : ''}`}>
              <b>Career services · 5 referral interviews</b>
              Five referral interviews for SDE or AI Engineer roles — plus resume &amp; LinkedIn
              review, a portfolio walkthrough and scored interview practice.
            </div>
          </div>

          <ol className={styles.ocSteps} aria-label="How you get there">
            {steps.map((step, index) => {
              const Icon = ICONS[step.icon];
              return (
                <li
                  key={step.h}
                  tabIndex={0}
                  className={index === activeIndex ? styles.on : ''}
                  onMouseEnter={() => setActiveIndex(index)}
                  onFocus={() => setActiveIndex(index)}
                  onClick={() => setActiveIndex(index)}
                >
                  <span className={styles.ocIc}>{Icon && <Icon size={20} aria-hidden="true" />}</span>
                  <div>
                    <div className={styles.ocWhen}>{step.when}</div>
                    <h4>{step.h}</h4>
                    <p>{step.p}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default CareerOutcomes;
